// NexSys — navigation, topology and command definitions. No sample metric values live here.
export const NAV = [
  { id: "command", label: "Command Center", short: "Command", icon: "LayoutDashboard" },
  { id: "cognitive", label: "Cognitive Core", short: "Cognitive", icon: "BrainCircuit" },
  { id: "continuum", label: "Continuum", short: "Continuum", icon: "History" },
  { id: "gateway", label: "AI Gateway", short: "Gateway", icon: "Waypoints" },
  { id: "decisions", label: "Decision Center", short: "Decisions", icon: "Lightbulb" },
  { id: "risk", label: "Risk Monitoring", short: "Risk", icon: "ShieldAlert" },
  { id: "timeline", label: "Timeline Forecast", short: "Timeline", icon: "GanttChartSquare" },
  { id: "twin", label: "Digital Twin", short: "Twin", icon: "Boxes" },
  { id: "predictive", label: "Predictive Analytics", short: "Predict", icon: "TrendingUp" },
  { id: "governance", label: "Governance", short: "Gov", icon: "Landmark" },
  { id: "signals", label: "AI Signals", short: "Signals", icon: "Radar" },
  { id: "integrations", label: "Data Sources", short: "Sources", icon: "Plug" },
  { id: "executions", label: "Executions", short: "Exec", icon: "PlayCircle", admin: true },
  { id: "access", label: "Access Log", short: "Access", icon: "Users", admin: true },
];

// digital twin nodes (enterprise systems). No hardcoded statuses: Data Center and Cloud are resolved from the
// live hosting self-probe (probe: "hosting"); every other node from its provisioning-queue label (not live).
export const TWIN_NODES = [
  { id: "erp", label: "ERP", x: 50, y: 22, connector: "sap" },
  { id: "cloud", label: "Cloud", x: 78, y: 40, connector: "azure", probe: "hosting" },
  { id: "data", label: "Data Fabric", x: 66, y: 70, connector: "data_fabric" },
  { id: "ai", label: "AI Gov", x: 34, y: 70, connector: "ai_governance" },
  { id: "sec", label: "Zero-Trust", x: 22, y: 40, connector: "zero_trust" },
  { id: "dc", label: "Data Center", x: 50, y: 50, connector: "data_center", probe: "hosting" },
  { id: "bi", label: "Power BI", x: 88, y: 66, connector: "power_bi" },
  { id: "crm", label: "Salesforce", x: 14, y: 66, connector: "salesforce" },
];
export const TWIN_EDGES = [
  ["dc", "erp"], ["dc", "cloud"], ["dc", "data"], ["dc", "ai"], ["dc", "sec"],
  ["cloud", "bi"], ["data", "ai"], ["sec", "crm"], ["erp", "cloud"], ["data", "bi"],
];

// timeline / gantt
export const FLAGS = [
  { key: "blue", label: "On Track", color: "#22d3ee" },
  { key: "green", label: "Ahead", color: "#34d399" },
  { key: "yellow", label: "At Risk", color: "#f59e0b" },
  { key: "red", label: "Blocked", color: "#ff3b6b" },
];
export const WEEKS = ["W1", "W2", "W3", "W4", "W5", "W6", "W7", "W8", "W9", "W10", "W11", "W12"];

export const COMMANDS = [
  { id: "analyze", label: "Analyze Portfolio", icon: "ScanSearch", msg: "Analyze the portfolio and surface the top risks." },
  { id: "brief", label: "Generate Executive Brief", icon: "FileText", msg: "Generate an executive brief." },
  { id: "predict", label: "Predict Delivery Risk", icon: "TriangleAlert", msg: "Predict delivery risk across programs." },
  { id: "bottleneck", label: "Identify Bottlenecks", icon: "Filter", msg: "Identify the current bottlenecks." },
  { id: "optimize", label: "Optimize Roadmap", icon: "Route", msg: "Optimize the roadmap and reallocate resources." },
  { id: "scenario", label: "Run Scenario Simulation", icon: "GitBranch", msg: "Run a scenario simulation for accelerated ERP testing." },
  { id: "board", label: "Produce Board Report", icon: "Presentation", msg: "Produce a board report." },
];

export const sevColor = (s) =>
  ({ critical: "#ff3b6b", high: "#ff9f43", moderate: "#22d3ee", low: "#34d399" }[s] || "#22d3ee");
export const flagColor = (f) =>
  (FLAGS.find((x) => x.key === f)?.color || "#22d3ee");

// Compact, factual summary of exactly what each NexSys view displays, so Chatty can cite
// on-screen numbers accurately (sent as `view_context` with every question).
export function viewDigest(viewId) {
  const label = NAV.find((n) => n.id === viewId)?.label || viewId;
  switch (viewId) {
    case "twin": {
      const nodes = TWIN_NODES.map((n) => n.label).join(", ");
      return `Digital Twin. Enterprise systems on the topology: ${nodes}. Only the Data Center node is LIVE: it is the application's self-probe of its own hosting environment (runtime, database and cloud provider/region measured on each load). Every other node has no live connector or health probe; its badge is a stored provisioning-queue label or UNCONFIGURED, never a live status.`;
    }
    case "governance":
      return "Governance. Status: unverified. No governed AI model registry is configured and no coverage figure is computed. The panel shows live configuration state of governance controls (not a compliance attestation). Governance monitoring is not implemented: no scheduled checks have run. Answer governance questions from live governed state, never from this summary.";
    case "integrations":
      return "Data Sources. 31 enterprise connectors across 6 domains: Business Systems (SAP, Oracle, Salesforce, ServiceNow, Power BI, Workday), Delivery Systems (Jira, Azure DevOps, Microsoft Project, Primavera P6, GitHub Enterprise), Security & Governance (Zero Trust, AI Governance, IAM/Identity, SIEM/Security Ops, Active Directory, Okta, Splunk), Infrastructure & Cloud (AWS, Azure, Google Cloud, Data Center, API Gateway, Integration Hub, Kubernetes), Data & Analytics (Data Fabric, Snowflake, Databricks, Tableau), Collaboration (SharePoint, Microsoft Teams). Each connector's lifecycle status comes from the Integration Provisioning Queue.";
    case "cognitive":
      return "Cognitive Core (Enterprise Cognitive Substrate — a component within NEXSYS). Positioning: SCIOUS (Computational Awareness Paradigm) → NEXSYS (Computational Intelligence Platform) → EDIOS (Enterprise Decision Intelligence OS). 10 specialized agents (Awareness, Semantic, Twin, Signal, Memory, Decision, Governance, Knowledge, Executive, Conversation) and 12 cognitive services orchestrate any decision into an EDIOS-governed Decision Object with full explainability, plus persistent enterprise memory.";
    default:
      return `${label} view. No fixed figures are shown on this screen; answer only from live data returned by the backend.`;
  }
}
